import { DateFormat, formatDate, now } from "common";
import { Dayjs } from "dayjs";
import { AffiliateStatus } from "types";
import create from "zustand";
import { DateFilterRange } from "./analyticsFilterStore";

const defaultRange = () => {
    return { from: now().subtract(1, "month").startOf("day"), to: now().endOf("day") };
};

export enum AffiliatePaymentStatus {
    ALL = "all",
    PENDING = "pending",
}

export interface PartnerCommissionsFilter {
    affiliateStatus: AffiliateStatus;
    affiliatePaymentStatus: AffiliatePaymentStatus;
    partners: { all: boolean; selected: ID[] };
}

const defaultFilters: PartnerCommissionsFilter = {
    affiliateStatus: AffiliateStatus.ACTIVE,
    affiliatePaymentStatus: AffiliatePaymentStatus.ALL,
    partners: { all: true, selected: [] },
};

interface PartnerCommissionsStoreState {
    to?: Dayjs;
    from?: Dayjs;
    getFrom: () => Dayjs;
    getTo: () => Dayjs;
    filters: PartnerCommissionsFilter;
    setFilters: (filters: Partial<PartnerCommissionsFilter>) => void;
    resetFilters: (filters?: Partial<PartnerCommissionsFilter>) => void;
    setFrom: (date: Dayjs) => void;
    setTo: (date: Dayjs) => void;
    getDateParams: (key?: DateFilterRange) => Record<string, string>;
}

export const usePartnerCommissionsFilterStore = create<PartnerCommissionsStoreState>((set, get) => ({
    from: undefined,
    to: undefined,
    filters: defaultFilters,

    getDateParams(key = DateFilterRange.CREATED_AT) {
        // Formatting date to DATE_ISO format instead of standard DATE_TIME_ISO as the API gives error for DATE_TIME_ISO
        const params = [
            formatDate(get().getFrom(), DateFormat.DATE_ISO),
            formatDate(get().getTo(), DateFormat.DATE_ISO),
        ];
        return { [key]: params.join(",") };
    },

    setFilters(filters: Partial<PartnerCommissionsFilter>) {
        set((state) => ({ filters: { ...state.filters, ...filters } }));
    },

    resetFilters(filters?: Partial<PartnerCommissionsFilter>) {
        set({ filters: { ...defaultFilters, ...filters } });
    },

    getFrom() {
        return get().from ?? defaultRange().from;
    },

    getTo() {
        return get().to ?? defaultRange().to;
    },

    setFrom(from: Dayjs) {
        set(() => ({ from }));
    },

    setTo(to: Dayjs) {
        set(() => ({ to }));
    },
}));
